import * as moment from "moment";
import { MBTI, PersonalityMajorType } from "src/app/interfaces/mbti";

export const queryStringToObject = (queryString: string): Record<string, string> => {
  const params = new URLSearchParams(queryString);
  const queryParams = {};

  params.forEach((value, key) => {
    queryParams[key] = value;
  });

  return queryParams;
}

export const isEmailValid = (email: string | undefined): boolean => {
  if (!email) {
    return false;
  }
  const emailValidRegex = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g;
  return emailValidRegex.test(email);
}

export const isObject = (value: any): boolean => {
  if (typeof value === 'object' && value !== null) {
    return true;
  }
  return false;
}

export const isEmptyObject = (value: object): boolean => {
  return value === null || Object.keys(value).length === 0;
}

export const customLocalStorage: Storage = (window.navigator?.cookieEnabled && window.localStorage) ? window.localStorage : {
  length: 0,
  clear: () => {},
  getItem: (key: string): string | null => { return null },
  key: (index: number): string | null => { return null },
  removeItem: (key: string): void => {},
  setItem: (key: string, value: string): void => {}
}

//**dataURL to blob**
export const dataURLtoBlob = (dataurl: any): Blob => {
  const arr = dataurl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while(n--){
      u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], {type:mime});
}

//**blob to dataURL**
export const blobToDataURL = (blob: Blob, callback) => {
  var a = new FileReader();
  a.onload = (e: any) => {callback(e.target.result);}
  a.readAsDataURL(blob);
}

// 최초 가입시 세팅해주는 uniqueName regex
export const initialUniqueNameRegex: RegExp = /^user_[0-9]+$/;

export const personalityMajorType = (mbti: MBTI): PersonalityMajorType => {
  const sjRegex = /(S[TF]J)+/;
  const nfRegex = /(NF)+/;
  const ntRegex = /(NT)+/;
  if (sjRegex.test(mbti)) {
    return 'SJ';
  }
  if (nfRegex.test(mbti)) {
    return 'NF';
  }
  if (ntRegex.test(mbti)) {
    return 'NT';
  }
  return 'SP';
}

export const baseColorCode = (mbti: MBTI): string => {
  const type: PersonalityMajorType = personalityMajorType(mbti)
  switch (type) {
    // NF
    case 'NF':
      return 'var(--teal-40)';
    // SJ
    case 'SJ':
      return 'var(--indigo-30)';
    // NT
    case 'NT':
      return 'var(--purple-30)';
    // SP
    case 'SP':
      return 'var(--orange-30)';
  }
}

export const strongColorCode = (mbti: MBTI): string => {
  const type: PersonalityMajorType = personalityMajorType(mbti)
  switch (type) {
    // NF
    case 'NF':
      return 'var(--teal-50)';
    // SJ
    case 'SJ':
      return 'var(--indigo-40)';
    // NT
    case 'NT':
      return 'var(--purple-40)';
    // SP
    case 'SP':
      return 'var(--orange-40)';
  }
}

export const weakColorCode = (mbti: MBTI): string => {
  const type: PersonalityMajorType = personalityMajorType(mbti)
  switch (type) {
    // NF
    case 'NF':
      return 'var(--teal-30)';
    // SJ
    case 'SJ':
      return 'var(--indigo-15)';
    // NT
    case 'NT':
      return 'var(--purple-15)';
    // SP
    case 'SP':
      return 'var(--orange-15)';
  }
}

export const surveyBackgroundColorCode = (mbti: MBTI): string => {
  const type: PersonalityMajorType = personalityMajorType(mbti);
  switch (type) {
    // NF
    case 'NF':
      return '#D5F0EB'; // teal10
    // SJ
    case 'SJ':
      return '#C9D9EF'; // indigo10
    // NT
    case 'NT':
      return '#DCD8E8'; // purple08
    // SP
    case 'SP':
      return '#FFD9C1'; // orange08
  }
}

export const getFormattedDate = (date: string, format: string = 'YYYY-MM-DD HH:mm:ss'): string => {
  return moment(date).format(format);
}

export const notAllowedProfileNameList: string[] = [
  'a', 'abandon', 'able', 'aboard', 'abort', 'about', 'above', 'abroad', 'absent', 'absolute', 'absorb', 'abstract', 'absurd', 'abundant', 'abuse', 'academy', 'accelerate', 'accent', 'accept', 'access', 'accident', 'accommodate', 'accompany', 'accomplish', 'accord', 'account', 'accumulate', 'accurate', 'accuse', 'achieve', 'acid', 'acknowledge', 'acquaint', 'acquire', 'acquisition', 'across', 'act', 'activate', 'actual', 'acute', 'adapt', 'add', 'addict', 'address', 'adequate', 'adjust', 'administer', 'admire', 'admission', 'admit', 'adolescent', 'adopt', 'adult', 'advance', 'advantage', 'adventure', 'adverse', 'advertize', 'advise', 'advocate', 'aesthetic', 'affair', 'affect', 'affection', 'affiliate', 'afford', 'afraid', 'after', 'afternoon', 'again', 'against', 'age', 'agency', 'agenda', 'agent', 'aggressive', 'ago', 'agree', 'agriculture', 'ahead', 'aid', 'aim', 'air', 'airplane', 'aircraft', 'airline', 'airport', 'aisle', 'alert', 'alien', 'alike', 'alive', 'all', 'allocate', 'allow', 'ally', 'almost', 'alone', 'along', 'alongside', 'aloud', 'already', 'alright', 'also', 'alter', 'alternative', 'although', 'altogether', 'always', 'amaze', 'ambassador', 'ambition', 'among', 'amount', 'amplify', 'amuse', 'analyze', 'anchor', 'ancient', 'and', 'angel', 'anger', 'angle', 'animal', 'anniversary', 'announce', 'annoy', 'annual', 'another', 'answer', 'ant', 'anticipate', 'anxiety', 'anxious', 'any', 'apart', 'apology', 'apparent', 'appeal', 'appear', 'apple', 'applicant', 'apply', 'appoint', 'appreciate', 'approach', 'appropriate', 'approve', 'approximate', 'architect', 'architecture', 'archive', 'area', 'argue', 'arise', 'arm', 'army', 'around', 'arrange', 'arrest', 'arrive', 'arrow', 'art', 'article', 'artificial', 'as', 'ash', 'aside', 'ask', 'asleep', 'aspect', 'aspire', 'assault', 'assemble', 'assert', 'assess', 'asset', 'assign', 'assist', 'associate', 'assume', 'assure', 'astonish', 'at', 'athlete', 'atmosphere', 'atom', 'attach', 'attack', 'attain', 'attempt', 'attend', 'attention', 'attitude', 'attorney', 'attract', 'attribute', 'auction', 'audience', 'aunt', 'authentic', 'author', 'autobiography', 'automatic', 'automobile', 'available', 'avenue', 'average', 'avoid', 'await', 'awake', 'award', 'aware', 'away', 'awesome', 'awful', 'awkward',
  'baby', 'back', 'background', 'bad', 'bake', 'balance', 'ball', 'balloon', 'ban', 'band', 'bang', 'bank', 'bankrupt', 'bar', 'bare', 'bargain', 'bark', 'barn', 'barrier', 'base', 'baseball', 'basic', 'basis', 'basket', 'basketball', 'bat', 'bath', 'bathroom', 'battery', 'battle', 'bay', 'be', 'beach', 'beam', 'bean', 'bear', 'beard', 'beast', 'beat', 'beauty', 'because', 'become', 'bed', 'bedroom', 'bee', 'beef', 'beer', 'before', 'beg', 'begin', 'behalf', 'behave', 'behavior', 'behind', 'believe', 'bell', 'belong', 'below', 'bend', 'beneath', 'benefit', 'berry', 'beside', 'bet', 'betray', 'between', 'beyond', 'bias', 'bible', 'bicycle', 'big', 'bill', 'billion', 'bin', 'bind', 'biography', 'biology', 'bird', 'birth', 'birthday', 'bishop', 'bit', 'bite', 'bitter', 'black', 'blame', 'blank', 'blanket', 'blast', 'blend', 'bless', 'blind', 'blink', 'block', 'blonde', 'blood', 'bloom', 'blossom', 'blow', 'blue', 'board', 'boat', 'body', 'boil', 'bold', 'bomb', 'bond', 'bone', 'book', 'boom', 'boost', 'boot', 'border', 'bore', 'borrow', 'boss', 'both', 'bother', 'bottle', 'bottom', 'bounce', 'boundary', 'bow', 'bowl', 'boy', 'brain', 'brake', 'branch', 'brand', 'brave', 'bread', 'break', 'breakfast', 'breast', 'breathe', 'breed', 'breeze', 'brew', 'brick', 'bride', 'bridge', 'brief', 'bright', 'brilliant', 'bring', 'broad', 'broadcast', 'brother', 'brown', 'brush', 'brute', 'bubble', 'budget', 'bug', 'build', 'bulk', 'bull', 'bullet', 'bully', 'bump', 'bunch', 'bundle', 'burden', 'burn', 'burst', 'bury', 'bush', 'business', 'busy', 'but', 'butcher', 'button', 'buy', 'buzz', 'by',
  'cab', 'cabin', 'cable', 'cage', 'calculate', 'calendar', 'call', 'calm', 'can', 'cancel', 'cancer', 'candidate', 'candy', 'canvas', 'cap', 'capable', 'cape', 'capital', 'captain', 'caption', 'capture', 'car', 'care', 'career', 'carrot', 'carry', 'cart', 'carve', 'case', 'cash', 'cast', 'castle', 'casual', 'cat', 'catch', 'category', 'cater', 'cathedral', 'cattle', 'causal', 'cause', 'caution', 'cave', 'cease', 'ceiling', 'celebrate', 'celebrity', 'cell', 'censor', 'century', 'certain', 'certificate', 'chain', 'chair', 'chairman', 'challenge', 'chamber', 'chance', 'change', 'chaos', 'character', 'characteristic', 'charge', 'charity', 'charm', 'chase', 'chat', 'cheap', 'check', 'cheek', 'cheer', 'chef', 'chemical', 'chemistry', 'chest', 'chew', 'chief', 'child', 'chill', 'chin', 'chip', 'choir', 'choose', 'chop', 'chorus', 'chronic', 'church', 'cigarette', 'cinema', 'circle', 'circulate', 'circumstance', 'cite', 'citizen', 'city', 'civil', 'claim', 'clap', 'clarify', 'clash', 'class', 'classic', 'classify', 'classroom', 'clause', 'clay', 'clean', 'clear', 'clerk', 'clever', 'client', 'cliff', 'climate', 'climb', 'cling', 'clinic', 'clip', 'clock', 'close', 'cloth', 'cloud', 'club', 'clue', 'cluster', 'coach', 'coal', 'coast', 'code', 'coin', 'coincide', 'cold', 'collaborate', 'collapse', 'collar', 'colleague', 'collect', 'college', 'colony', 'color', 'column', 'combat', 'combine', 'come', 'comedy', 'comfort', 'command', 'commence', 'comment', 'commerce', 'commission', 'commit', 'committee', 'commodity', 'communicate', 'communist', 'community', 'companion', 'company', 'compare', 'compatible', 'compel', 'compensate', 'compete', 'competent', 'compile', 'complain', 'complement', 'complete', 'complex', 'complicate', 'component', 'compose', 'compound', 'comprehensive', 'comprise', 'compromise', 'compulsory', 'conceal', 'conceive', 'concentrate', 'concept', 'concern', 'concert', 'conclude', 'concrete', 'condemn', 'condition', 'conduct', 'confer', 'confess', 'confident', 'confine', 'confirm', 'conflict', 'conform', 'confront', 'confuse', 'congratulate', 'congress', 'connect', 'conscience', 'conscious', 'consensus', 'consent', 'conserve', 'consider', 'consist', 'consistent', 'constant', 'constitute', 'constrain', 'construct', 'consult', 'consume', 'contact', 'contain', 'contemporary', 'contend', 'content', 'contest', 'context', 'continent', 'continue', 'contract', 'contradict', 'contrary', 'contrast', 'contribute', 'control', 'controversy', 'convenient', 'convention', 'conversation', 'convert', 'convey', 'convict', 'convince', 'cook', 'cookie', 'cool', 'cooperate', 'coordinate', 'cop', 'cope', 'copy', 'copyright', 'cord', 'core', 'corn', 'corner', 'corporate', 'correct', 'correspond', 'corridor', 'corrupt', 'cost', 'costume', 'cottage', 'cotton', 'couch', 'cough', 'could', 'council', 'counsel', 'count', 'counter', 'counterpart', 'country', 'countryside', 'county', 'couple', 'courage', 'cousin', 'cover', 'cow', 'cowboy', 'crack', 'craft', 'crash', 'crawl', 'crazy', 'create', 'creature', 'credible', 'credit', 'creek', 'creep', 'crew', 'cricket', 'crime', 'criminal', 'crisis', 'crisp', 'criteria', 'critic', 'criticize', 'criticism', 'crop', 'cross', 'crowd', 'crown', 'crucial', 'cruel', 'cruise', 'crush', 'cry', 'crystal', 'cultivate', 'culture', 'cupboard', 'cure', 'curious', 'curl', 'currency', 'current', 'curriculum', 'curry', 'curse', 'curtain', 'curve', 'custody', 'custom', 'customer', 'cut', 'cute', 'cycle', 'cynical',
  'dad', 'dairy', 'dam', 'damage', 'damp', 'dance', 'danger', 'dare', 'dark', 'darling', 'dash', 'database', 'date', 'daughter', 'dawn', 'day', 'dead', 'deal', 'death', 'debate', 'debt', 'decade', 'decay', 'decent', 'decide', 'decision', 'deck', 'declare', 'decline', 'decorate', 'decrease', 'dedicate', 'deep', 'defeat', 'defense', 'defend', 'defendant', 'deficiency', 'deficit', 'define', 'definite', 'degrade', 'degree', 'delay', 'delegate', 'delete', 'deliberate', 'delicate', 'delicious', 'delight', 'deliver', 'demand', 'democracy', 'democrat', 'demon', 'demonstrate', 'dense', 'dentist', 'deny', 'department', 'departure', 'depend', 'dependence', 'depict', 'deposit', 'depress', 'deprive', 'derive', 'descend', 'describe', 'description', 'desert', 'deserve', 'design', 'designate', 'desire', 'desk', 'despair', 'desperate', 'despite', 'destination', 'destiny', 'destroy', 'destruction', 'detach', 'detail', 'detect', 'determine', 'develop', 'device', 'devil', 'devise', 'devote', 'diabetes', 'dialogue', 'diary', 'dictate', 'dictionary', 'die', 'different', 'difficult', 'dig', 'dignity', 'dimension', 'diminish', 'dine', 'dinner', 'dip', 'diplomat', 'direct', 'dirty', 'disabled', 'disadvantage', 'disagree', 'disappear', 'disappoint', 'disaster', 'discharge', 'discipline', 'disclose', 'discount', 'discourse', 'discover', 'discriminate', 'discuss', 'disease', 'disgust', 'dish', 'dismiss', 'disorder', 'displace', 'display', 'dispose', 'dispute', 'disrupt', 'distant', 'distort', 'distract', 'distribute', 'district', 'disturb', 'dive', 'diverse', 'divide', 'divine', 'divorce', 'do', 'doctor', 'document', 'dog', 'doll', 'dolphin', 'domain', 'domestic', 'dominant', 'donate', 'door', 'dose', 'dot', 'double', 'doubt', 'down', 'dozen', 'draft', 'drag', 'drain', 'draw', 'drawer', 'dread', 'dream', 'drink', 'drive', 'drop', 'drown', 'drug', 'dry', 'dual', 'duck', 'due', 'dull', 'dump', 'during', 'dust', 'duty', 'dwell', 'dynamic',
  'each', 'eager', 'ear', 'early', 'earn', 'earth', 'ease', 'east', 'easy', 'eat', 'economy', 'edge', 'edit', 'educate', 'effect', 'effective', 'efficient', 'effort', 'egg', 'either', 'elaborate', 'elect', 'electric', 'electronic', 'elegant', 'element', 'elementary', 'elephant', 'eliminate', 'else', 'embarrass', 'embassy', 'embrace', 'emerge', 'emergency', 'emit', 'emotion', 'emphasis', 'empire', 'employ', 'empty', 'enclose', 'encounter', 'encourage', 'end', 'endure', 'enemy', 'enforce', 'engage', 'engine', 'engineer', 'enhance', 'enjoy', 'enormous', 'enough', 'enroll', 'ensure', 'enter', 'enterprise', 'entertain', 'enthusiastic', 'entire', 'entitle', 'entry', 'envelope', 'environment', 'envy', 'episode', 'equal', 'equate', 'equipment', 'era', 'eraser', 'erect', 'error', 'escape', 'escort', 'especially', 'essential', 'establish', 'estate', 'estimate', 'etc', 'ethical', 'ethnic', 'evacuate', 'evaluate', 'eve', 'even', 'evening', 'eventually', 'ever', 'every', 'evident', 'evil', 'evolution', 'evolve', 'exact', 'exaggerate', 'exam', 'examine', 'example', 'exceed', 'excellent', 'except', 'excess', 'exchange', 'excite', 'exclude', 'exclusive', 'excuse', 'executive', 'exercise', 'exhaust', 'exhibit', 'exist', 'exit', 'exotic', 'expand', 'expect', 'expense', 'expensive', 'experience', 'experiment', 'expert', 'expertise', 'explain', 'explicit', 'explode', 'explore', 'export', 'expose', 'express', 'extend', 'extent', 'external', 'extinct', 'extra', 'extract', 'extraordinary', 'extreme', 'eye', 'eyebrow',
  'fabric', 'face', 'facilitate', 'facility', 'fact', 'factor', 'factory', 'faculty', 'fade', 'fail', 'faint', 'fair', 'faith', 'fall', 'false', 'FALSE', 'fame', 'familiar', 'family', 'famous', 'fan', 'fancy', 'fantastic', 'far', 'fare', 'farm', 'fascinate', 'fast', 'fasten', 'fat', 'fatal', 'fate', 'father', 'fault', 'favor', 'favorite', 'fear', 'feature', 'federal', 'fee', 'feed', 'feel', 'fellow', 'female', 'ferry', 'fertile', 'fever', 'few', 'fiber', 'field', 'fierce', 'fight', 'figure', 'file', 'fill', 'filter', 'final', 'finance', 'find', 'fine', 'finger', 'finish', 'finite', 'fire', 'firm', 'fish', 'fist', 'fit', 'fix', 'flag', 'flame', 'flash', 'flat', 'flavor', 'flaw', 'flee', 'flesh', 'flexible', 'flight', 'flip', 'float', 'flock', 'flood', 'floor', 'flourish', 'flow', 'flower', 'fluid', 'flush', 'fly', 'focus', 'fog', 'fold', 'folk', 'follow', 'fond', 'food', 'fool', 'foot', 'football', 'for', 'forbid', 'force', 'forecast', 'forehead', 'foreign', 'forest', 'forever', 'forget', 'forgive', 'form', 'formal', 'format', 'formation', 'former', 'formula', 'forth', 'fortunate', 'fortune', 'forum', 'forward', 'foster', 'foundation', 'fountain', 'fox', 'fraction', 'frame', 'framework', 'frankly', 'free', 'freeze', 'frequent', 'fresh', 'friend', 'fright', 'frog', 'from', 'front', 'frost', 'frown', 'fruit', 'frustrate', 'fry', 'fuel', 'fulfil', 'full', 'fun', 'function', 'fund', 'fundamental', 'funeral', 'fur', 'furious', 'furnish', 'furniture', 'furthermore', 'fuse', 'future',
  'gain', 'gamble', 'gang', 'gap', 'garage', 'garden', 'gasoline', 'gate', 'gather', 'gaze', 'gear', 'gender', 'gene', 'general', 'generate', 'generation', 'generous', 'genius', 'genre', 'gentle', 'gentleman', 'genuine', 'geography', 'geology', 'gesture', 'get', 'ghost', 'giant', 'gift', 'giraffe', 'girl', 'give', 'glad', 'glance', 'glare', 'glass', 'glimpse', 'global', 'glory', 'glove', 'glow', 'glue', 'go', 'goal', 'goat', 'god', 'gold', 'good', 'goodbye', 'goods', 'gorgeous', 'govern', 'grab', 'grace', 'grade', 'gradual', 'graduate', 'grain', 'grand', 'grandfather', 'grant', 'grape', 'graphic', 'grasp', 'grass', 'grateful', 'grave', 'great', 'greed', 'green', 'greet', 'grey', 'grief', 'grip', 'grocery', 'gross', 'ground', 'group', 'grow', 'guarantee', 'guardian', 'guess', 'guest', 'guide', 'guideline', 'guilty', 'gulf', 'gun', 'guy', 'gymnasium',
  'habit', 'habitat', 'hair', 'half', 'hall', 'halt', 'hammer', 'hand', 'handicap', 'handle', 'handsome', 'hang', 'happen', 'happy', 'harbor', 'hard', 'hardly', 'harm', 'harsh', 'harvest', 'haste', 'hat', 'hate', 'haunt', 'have', 'hazard', 'he', 'head', 'headache', 'headquarters', 'heal', 'health', 'hear', 'heart', 'heat', 'heaven', 'heavy', 'heel', 'height', 'heir', 'helicopter', 'hell', 'hello', 'help', 'hence', 'here', 'heritage', 'hero', 'hesitate', 'hide', 'high', 'highway', 'hill', 'hip', 'hire', 'history', 'hit', 'hobby', 'hold', 'hole', 'holiday', 'holy', 'home', 'homework', 'honest', 'honey', 'honor', 'hook', 'hope', 'horizon', 'horn', 'horror', 'horse', 'hospital', 'host', 'hostage', 'hostile', 'hot', 'hour', 'house', 'household', 'how', 'however', 'hug', 'huge', 'human', 'humor', 'hundred', 'hungry', 'hunt', 'hurry', 'hurt', 'husband', 'hut', 'hypothesis',
  'i', 'ice', 'idea', 'ideal', 'identical', 'identify', 'ideology', 'if', 'ignore', 'ill', 'illusion', 'illustrate', 'imagine', 'imitate', 'immediate', 'immense', 'immigrate', 'immune', 'impact', 'imperial', 'implement', 'imply', 'import', 'important', 'impose', 'impress', 'improve', 'in', 'incentive', 'incident', 'incline', 'include', 'income', 'incorporate', 'increase', 'incredible', 'indeed', 'independent', 'index', 'indicate', 'individual', 'induce', 'industry', 'inevitable', 'infant', 'infect', 'infer', 'inflate', 'influence', 'inform', 'informal', 'ingredient', 'inhabit', 'inherent', 'inhibit', 'initial', 'initiate', 'inject', 'injure', 'inn', 'innocent', 'innovate', 'input', 'inquire', 'insect', 'insert', 'inside', 'insight', 'insist', 'inspect', 'inspire', 'install', 'instance', 'instant', 'instead', 'instinct', 'institute', 'instruct', 'instrument', 'insult', 'insure', 'integrate', 'intellectual', 'intelligent', 'intend', 'intense', 'intent', 'interact', 'interest', 'interfere', 'intermediate', 'internal', 'international', 'interpret', 'interrupt', 'interval', 'intervene', 'intimate', 'into', 'intrigue', 'introduce', 'invade', 'invent', 'invest', 'investigate', 'invite', 'involve', 'iron', 'irony', 'irritate', 'island', 'isolate', 'it', 'item',
  'jail', 'jar', 'jaw', 'jeans', 'jet', 'job', 'jog', 'join', 'joint', 'joke', 'journal', 'journey', 'joy', 'judge', 'judicial', 'junior', 'jury', 'just', 'justice', 'justify',
  'keen', 'keep', 'key', 'kick', 'kid', 'kill', 'kind', 'king', 'kit', 'kitchen', 'knee', 'knife', 'knight', 'knock', 'knot', 'know',
  'label', 'labor', 'laboratory', 'lack', 'ladder', 'lady', 'lake', 'lamb', 'lamp', 'land', 'landscape', 'lane', 'language', 'lap', 'large', 'last', 'late', 'latter', 'laugh', 'launch', 'laundry', 'law', 'lawn', 'lawyer', 'lay', 'layer', 'lazy', 'lead', 'leaf', 'leak', 'lean', 'leap', 'learn', 'lease', 'leather', 'leave', 'lecture', 'left', 'leg', 'legal', 'legend', 'legislate', 'legitimate', 'leisure', 'lend', 'lesson', 'let', 'letter', 'level', 'liberal', 'liberty', 'library', 'license', 'lid', 'lie', 'lift', 'light', 'like', 'likely', 'likewise', 'limit', 'line', 'linguistic', 'link', 'lion', 'lip', 'liquid', 'list', 'listen', 'literal', 'literature', 'little', 'live', 'livingroom', 'load', 'loan', 'local', 'locate', 'lock', 'log', 'logic', 'lone', 'long', 'look', 'loose', 'lose', 'loss', 'lot', 'loud', 'lounge', 'love', 'low', 'loyal', 'luck', 'lump', 'lunch', 'luxury',
  'machine', 'mad', 'magazine', 'magnet', 'magnificent', 'mail', 'main', 'maintain', 'major', 'majority', 'make', 'male', 'man', 'manage', 'manifest', 'manipulate', 'manner', 'manufacture', 'many', 'map', 'margin', 'marine', 'mark', 'marry', 'marvel', 'mass', 'master', 'match', 'mate', 'material', 'mathematics', 'matter', 'mature', 'maximum', 'may', 'maybe', 'mayor', 'meal', 'mean', 'meantime', 'meanwhile', 'measure', 'meat', 'mechanic', 'mechanism', 'mediate', 'medical', 'medicine', 'medieval', 'medium', 'meet', 'melt', 'memory', 'mental', 'mention', 'merchant', 'mere', 'merge', 'merit', 'mess', 'metal', 'method', 'metropolitan', 'microphone', 'microwave', 'middle', 'might', 'migrate', 'mild', 'military', 'milk', 'mill', 'million', 'mind', 'miner', 'mineral', 'minimal', 'minimum', 'ministry', 'minor', 'minute', 'miracle', 'mirror', 'miss', 'missile', 'mission', 'mistake', 'mix', 'mobile', 'mock', 'mode', 'moderate', 'modern', 'modest', 'modify', 'moisture', 'molecule', 'moment', 'money', 'monitor', 'monkey', 'monster', 'month', 'monument', 'mood', 'moon', 'moral', 'moreover', 'morning', 'mortal', 'mother', 'motion', 'motive', 'mount', 'mountain', 'mouse', 'mouth', 'move', 'movie', 'much', 'mud', 'multiple', 'multiply', 'murder', 'muscle', 'museum', 'mushroom', 'music', 'must', 'mutual', 'myth',
  'nail', 'naive', 'naked', 'name', 'nanny', 'narrate', 'narrow', 'nasty', 'nation', 'native', 'nature', 'navy', 'near', 'neat', 'necessary', 'neck', 'need', 'needle', 'negative', 'neglect', 'negotiate', 'neighbor', 'neither', 'nephew', 'nervous', 'nest', 'net', 'network', 'neutral', 'never', 'nevertheless', 'new', 'newspaper', 'next', 'nice', 'night', 'nightmare', 'no', 'noble', 'nobody', 'nod', 'noise', 'nominate', 'none', 'nonetheless', 'nonsense', 'noon', 'nor', 'norm', 'normal', 'north', 'nose', 'not', 'note', 'nothing', 'notice', 'notion', 'novel', 'now', 'nowadays', 'nowhere', 'nuclear', 'number', 'numerous', 'nun', 'nurse', 'nut',
  'oak', 'obey', 'object', 'objective', 'oblige', 'observe', 'obsess', 'obtain', 'obvious', 'occasion', 'occupation', 'occupy', 'occur', 'ocean', 'odd', 'of', 'off', 'offend', 'offer', 'office', 'officer', 'official', 'often', 'oil', 'old', 'olive', 'on', 'once', 'one', 'only', 'open', 'operate', 'opinion', 'opportunity', 'oppose', 'optimist', 'option', 'or', 'oral', 'orbit', 'orchestra', 'order', 'ordinary', 'organ', 'organic', 'organize', 'orient', 'origin', 'other', 'otherwise', 'ought', 'ounce', 'out', 'outcome', 'outline', 'output', 'outrage', 'outstanding', 'over', 'overall', 'overcome', 'overhead', 'overlap', 'overlook', 'overnight', 'oversea', 'overwhelm', 'owe', 'own',
  'pace', 'pack', 'packet', 'pad', 'pain', 'paint', 'pair', 'palace', 'pale', 'palm', 'pan', 'panel', 'panic', 'pants', 'paper', 'paragraph', 'parallel', 'pardon', 'parent', 'park', 'parliament', 'part', 'participate', 'particle', 'particular', 'pass', 'passage', 'passenger', 'passion', 'passport', 'past', 'pat', 'patch', 'patent', 'path', 'patient', 'patrol', 'pattern', 'pause', 'pave', 'pay', 'peace', 'peak', 'pear', 'peasant', 'peel', 'peer', 'penalty', 'pencil', 'people', 'pepper', 'per', 'perceive', 'perfect', 'perform', 'perhaps', 'period', 'permanent', 'permit', 'persist', 'person', 'personality', 'perspective', 'persuade', 'pet', 'phase', 'phenomenon', 'philosophy', 'photograph', 'phrase', 'physical', 'physics', 'pick', 'picnic', 'picture', 'piece', 'pig', 'pile', 'pill', 'pin', 'pinch', 'pine', 'pink', 'pioneer', 'pitch', 'pity', 'place', 'plain', 'plan', 'planet', 'plant', 'plate', 'platform', 'play', 'pleasant', 'please', 'pleasure', 'plenty', 'plot', 'plus', 'pm', 'pocket', 'poem', 'poet', 'point', 'poison', 'polar', 'pole', 'police', 'policy', 'polish', 'polite', 'politics', 'poll', 'pollute', 'pond', 'pool', 'poor', 'pop', 'pope', 'popular', 'population', 'pork', 'port', 'portion', 'portrait', 'pose', 'position', 'positive', 'possess', 'possible', 'post', 'poster', 'pot', 'potato', 'potential', 'pour', 'powder', 'power', 'practical', 'practice', 'praise', 'pray', 'preach', 'precede', 'precious', 'precise', 'predator', 'predict', 'prefer', 'pregnant', 'prejudice', 'premier', 'premium', 'prepare', 'prescribe', 'presence', 'present', 'preserve', 'president', 'press', 'presume', 'pretend', 'pretty', 'prevail', 'prevent', 'previous', 'prey', 'price', 'pride', 'priest', 'primary', 'prime', 'primitive', 'prince', 'principal', 'principle', 'print', 'prior', 'prison', 'privacy', 'private', 'privilege', 'prize', 'probably', 'problem', 'procedure', 'proceed', 'process', 'produce', 'profession', 'professor', 'profile', 'profit', 'profound', 'progress', 'prohibit', 'prominent', 'promise', 'promote', 'prompt', 'pronounce', 'proof', 'proper', 'property', 'proportion', 'propose', 'prospect', 'prosper', 'protect', 'protein', 'protest', 'proud', 'prove', 'provide', 'province', 'provoke', 'psychology', 'pub', 'public', 'publish', 'pull', 'pump', 'punch', 'punish', 'pupil', 'puppy', 'purchase', 'pure', 'purple', 'purpose', 'pursue', 'push', 'put', 'puzzle',
  'qualify', 'quality', 'quantity', 'quarter', 'queen', 'question', 'questionnaire', 'quick', 'quiet', 'quit', 'quite', 'quote',
  'rabbit', 'race', 'racial', 'rage', 'rail', 'rain', 'rainbow', 'raise', 'rally', 'random', 'range', 'rank', 'rapid', 'rare', 'rat', 'rate', 'rather', 'rational', 'raw', 'reach', 'react', 'read', 'ready', 'real', 'realize', 'rear', 'reason', 'rebel', 'recall', 'receipt', 'receive', 'recent', 'reception', 'recipe', 'recognize', 'recommend', 'record', 'recover', 'recruit', 'recycle', 'red', 'reduce', 'refer', 'refine', 'reflect', 'reform', 'refrigerator', 'refuse', 'regard', 'region', 'register', 'regret', 'regular', 'regulate', 'reinforce', 'reject', 'relate', 'relative', 'relax', 'release', 'relevant', 'relieve', 'religion', 'reluctant', 'rely', 'remain', 'remark', 'remedy', 'remember', 'remind', 'remote', 'remove', 'rent', 'repair', 'repeat', 'replace', 'reply', 'report', 'represent', 'republic', 'reputation', 'request', 'require', 'rescue', 'research', 'resemble', 'reserve', 'reside', 'resign', 'resist', 'resolve', 'resort', 'resource', 'respect', 'respective', 'respond', 'responsible', 'rest', 'restaurant', 'restore', 'restrain', 'restrict', 'restroom', 'result', 'resume', 'retail', 'retain', 'retire', 'retreat', 'return', 'reveal', 'revenge', 'reverse', 'review', 'revise', 'revive', 'revolution', 'reward', 'rhythm', 'rice', 'rich', 'rid', 'ride', 'ridiculous', 'right', 'ring', 'riot', 'rise', 'risk', 'rival', 'river', 'road', 'roar', 'roast', 'rob', 'rock', 'rod', 'role', 'roll', 'romantic', 'roof', 'room', 'root', 'rope', 'rot', 'rough', 'round', 'route', 'routine', 'row', 'royal', 'rub', 'rubber', 'rude', 'ruin', 'rule', 'rumor', 'run', 'rural', 'rush',
  'sack', 'sacred', 'sacrifice', 'sad', 'safe', 'sail', 'salary', 'sale', 'salon', 'salt', 'same', 'sand', 'satellite', 'satisfy', 'save', 'say', 'scale', 'scan', 'scandal', 'scarce', 'scare', 'scatter', 'scene', 'scheme', 'scholar', 'school', 'science', 'scissors', 'scold', 'scope', 'score', 'scramble', 'scratch', 'scream', 'screen', 'screw', 'scrub', 'sculpture', 'sea', 'seal', 'search', 'season', 'seat', 'secret', 'secretary', 'sector', 'secure', 'see', 'seed', 'seek', 'seem', 'seize', 'select', 'self', 'sell', 'send', 'senior', 'sensation', 'sense', 'sensible', 'sentence', 'sentiment', 'separate', 'sequence', 'series', 'serious', 'serve', 'session', 'setting', 'settings', 'settle', 'several', 'severe', 'sew', 'sex', 'shade', 'shadow', 'shake', 'shall', 'shallow', 'shame', 'shape', 'share', 'sharp', 'shave', 'she', 'sheep', 'sheet', 'shelf', 'shell', 'shelter', 'shield', 'shift', 'shine', 'ship', 'shock', 'shoe', 'shoot', 'shop', 'shore', 'short', 'should', 'shoulder', 'shout', 'show', 'shower', 'shrink', 'shut', 'shy', 'sick', 'side', 'sigh', 'sight', 'sign', 'significant', 'silent', 'silk', 'silly', 'silver', 'similar', 'simple', 'simulate', 'simultaneous', 'sin', 'since', 'sing', 'single', 'sink', 'sister', 'sit', 'site', 'situation', 'size', 'skill', 'skin', 'skip', 'skirt', 'sky', 'slaughter', 'slave', 'sleep', 'slice', 'slide', 'slight', 'slim', 'slip', 'slope', 'slow', 'small', 'smart', 'smash', 'smell', 'smile', 'smoke', 'smooth', 'snake', 'snap', 'sneak', 'sniff', 'snow', 'so', 'soak', 'soap', 'soccer', 'social', 'society', 'sociology', 'sock', 'soft', 'software', 'soil', 'soldier', 'sole', 'solid', 'solo', 'solve', 'some', 'somewhat', 'son', 'song', 'soon', 'sophisticated', 'sore', 'sorry', 'sort', 'soul', 'sound', 'sour', 'source', 'south', 'space', 'span', 'spare', 'spark', 'speak', 'special', 'species', 'specific', 'spectacle', 'spectrum', 'speech', 'speed', 'spell', 'spend', 'sphere', 'spill', 'spin', 'spirit', 'spit', 'spite', 'splash', 'split', 'spoil', 'spoon', 'spot', 'spouse', 'spread', 'square', 'squeeze', 'stable', 'stack', 'stage', 'stain', 'stairs', 'stamp', 'stand', 'standard', 'stare', 'start', 'starve', 'state', 'station', 'statistic', 'statue', 'status', 'stay', 'steady', 'steal', 'steam', 'steel', 'steep', 'stem', 'step', 'stick', 'stiff', 'still', 'stimulate', 'stir', 'stitch', 'stock', 'stomach', 'stone', 'stop', 'store', 'storm', 'story', 'stove', 'straight', 'strain', 'strange', 'strategy', 'straw', 'strawberry', 'stream', 'street', 'stress', 'stretch', 'strict', 'strike', 'string', 'strip', 'stripe', 'stroke', 'strong', 'structure', 'struggle', 'student', 'study', 'stuff', 'subject', 'subjective', 'submarine', 'submit', 'subscribe', 'substance', 'substantial', 'substitute', 'subtle', 'suburb', 'subway', 'succeed', 'such', 'suck', 'sudden', 'suffer', 'sufficient', 'sugar', 'suggest', 'suicide', 'suit', 'suite', 'sum', 'summary', 'summit', 'sun', 'super', 'superb', 'superior', 'supervise', 'supper', 'supplement', 'supply', 'support', 'suppose', 'sure', 'surface', 'surgery', 'surprise', 'surrender', 'surround', 'survey', 'survive', 'suspect', 'suspend', 'sustain', 'swallow', 'swear', 'sweat', 'sweep', 'sweet', 'swell', 'swift', 'swim', 'swing', 'switch', 'symbol', 'sympathy', 'symphony', 'symptom', 'system',
  'table', 'tackle', 'tag', 'tail', 'take', 'tale', 'talent', 'talk', 'tall', 'tap', 'tape', 'target', 'task', 'taste', 'tattoo', 'tax', 'tea', 'teach', 'tear', 'tease', 'teen', 'teenage', 'telegraph', 'telephone', 'tell', 'temperature', 'temple', 'temporary', 'tempt', 'tenant', 'tend', 'tender', 'tense', 'term', 'terminal', 'terminate', 'terrace', 'terrible', 'terrific', 'territory', 'test', 'testify', 'text', 'textbook', 'than', 'thank', 'that', 'the', 'theater', 'theme', 'then', 'theory', 'therapy', 'there', 'therefore', 'they', 'thick', 'thief', 'thin', 'thing', 'think', 'thirst', 'this', 'thorough', 'though', 'thousand', 'thread', 'threat', 'thrill', 'throat', 'through', 'throw', 'thumb', 'thus', 'tick', 'tide', 'tidy', 'tie', 'tiger', 'tight', 'till', 'timber', 'time', 'tin', 'tiny', 'tip', 'tire', 'tissue', 'title', 'to', 'tobacco', 'today', 'toe', 'together', 'toilet', 'tomorrow', 'tone', 'tongue', 'tonight', 'too', 'tool', 'tooth', 'top', 'torture', 'toss', 'total', 'touch', 'tough', 'tour', 'toward', 'tower', 'town', 'toxic', 'toy', 'trace', 'trade', 'tradition', 'traffic', 'tragic', 'trail', 'train', 'transact', 'transfer', 'transform', 'transition', 'translate', 'transmit', 'transport', 'trap', 'travel', 'tray', 'treasure', 'treat', 'treaty', 'tree', 'tremendous', 'trend', 'trial', 'triangle', 'tribe', 'trick', 'trigger', 'trim', 'trip', 'triumph', 'troop', 'trophy', 'trouble', 'trouser', 'true', 'TRUE', 'trunk', 'trust', 'truth', 'try', 'tube', 'tune', 'tunnel', 'turn', 'turnover', 'tutor', 'twice', 'twin', 'twist', 'type', 'typical',
  'ugly', 'ultimate', 'umbrella', 'uncle', 'under', 'undergo', 'underlie', 'undermine', 'understand', 'undertake', 'uniform', 'unify', 'union', 'unique', 'unit', 'unite', 'universe', 'university', 'unless', 'until', 'up', 'update', 'upon', 'upper', 'upset', 'upward', 'urban', 'urge', 'urgent', 'use', 'usual', 'utilize', 'utter',
  'vacation', 'vacuum', 'vague', 'valid', 'valley', 'value', 'van', 'vanish', 'various', 'vary', 'vast', 'vegetable', 'vehicle', 'venture', 'verbal', 'verse', 'version', 'versus', 'vertical', 'very', 'vessel', 'veteran', 'veterinarian', 'via', 'vice', 'victim', 'victory', 'view', 'vigorous', 'village', 'violent', 'virgin', 'virtual', 'virtue', 'visible', 'visit', 'visual', 'vital', 'vivid', 'vocabulary', 'vocation', 'voice', 'volume', 'voluntary', 'vote', 'voyage',
  'wage', 'wagon', 'wait', 'wake', 'walk', 'wall', 'wander', 'want', 'war', 'warehouse', 'warm', 'warn', 'warrant', 'warrior', 'wash', 'waste', 'watch', 'water', 'watermelon', 'wave', 'way', 'we', 'weak', 'wealth', 'weapon', 'wear', 'weather', 'weave', 'wedding', 'weed', 'week', 'weekend', 'weigh', 'weight', 'weird', 'welcome', 'welfare', 'well', 'west', 'wet', 'whale', 'what', 'wheat', 'wheel', 'when', 'where', 'whereas', 'whether', 'which', 'while', 'whip', 'whisper', 'whistle', 'white', 'who', 'whole', 'why', 'wicked', 'wide', 'widespread', 'widow', 'wife', 'wild', 'will', 'win', 'wind', 'window', 'wing', 'wipe', 'wire', 'wise', 'wish', 'wit', 'with', 'withdraw', 'within', 'without', 'witness', 'woman', 'wonder', 'wood', 'wool', 'word', 'work', 'world', 'worry', 'worship', 'worth', 'would', 'wound', 'wrap', 'wreck', 'write', 'wrong',
  'year','yell','yellow','yes','yesterday','yet','yield','you','young',
  'zebra', 'zone', 'zoo',
  'answer-card', 'quiz-report', 'quiz-create', 'quiz', 'my', 'header', 'footer', 'oauth', 'kakao', 'login', 
  'advertise',
  'plane',
  'am',
  'analyse',
  'behaviour',
  'cheque',
  'colour',
  'cooky',
  'criticise',
  'defence',
  'dialog',
  'enrol',
  'examination',
  'favour',
  'favourite',
  'fibre',
  'flavour',
  'petrol',
  'gray',
  'gym',
  'harbour',
  'hey',
  'honour',
  'humour',
  'enquire',
  'labour',
  'lab',
  'licence',
  'maths',
  'neighbour',
  'nope',
  'organise',
  'overseas',
  'practise',
  'realise',
  'fridge',
  'rumour',
  'sculpt',
  'theatre',
  'tyre',
  'towards',
  'upwards',
  'utilise',
  'yeah',
  'aeroplane',
  'hi',
  'math',
  'nay',
  'yep',
  'types',
  'admin',
  'adc',
  'alu',
  'ansi',
  'ascii',
  'abstraction',
  'accuracy',
  'actuator',
  'algorithm',
  'alphanumeric',
  'analog',
  'analysis',
  'anomaly',
  'array',
  'assembler',
  'assembling',
  'assertion',
  'asynchronous',
  'audit',
  'bios',
  'bps',
  'bandwidth',
  'baseline',
  'batch',
  'baud',
  'benchmark',
  'binary',
  'blueprint',
  'boolean',
  'bootstrap',
  'buffer',
  'bus',
  'byte',
  'cad',
  'cam',
  'ccitt',
  'cisc',
  'cmos',
  'cots',
  'cpu',
  'crc',
  'crt',
  'c',
  'c++',
  'calibration',
  'certification',
  'checksum',
  'cobol',
  'coding',
  'comparator',
  'compatibility',
  'compilation',
  'compiler',
  'compiling',
  'completeness',
  'complexity',
  'computer',
  'configuration',
  'consistency',
  'controller',
  'conversational',
  'coroutine',
  'correctness',
  'criticality',
  'cursor',
  'dac',
  'dfd',
  'dma',
  'dos',
  'data',
  'debugging',
  'default',
  'defect',
  'delimiter',
  'demodulate',
  'demodulation',
  'dependability',
  'developer',
  'diagnostic',
  'digital',
  'disk',
  'diskette',
  'documentation',
  'drift',
  'driver',
  'ebcdic',
  'eeprom',
  'emi',
  'eprom',
  'esd',
  'esdi',
  'editing',
  'emulation',
  'emulator',
  'encapsulation',
  'exception',
  'fdd',
  'fips',
  'fmea',
  'fmeca',
  'fta',
  'ftp',
  'failure',
  'firmware',
  'fortran',
  'gb',
  'gigabyte',
  'graph',
  'hdd',
  'hipo',
  'hz',
  'handshake',
  'hardware',
  'hertz',
  'hexadecimal',
  'ic',
  'ide',
  'iec',
  'ieee',
  'iso',
  'implementation',
  'inspection',
  'installation',
  'instruction',
  'instrumentation',
  'interactive',
  'interface',
  'interpreter',
  'jcl',
  'kb',
  'kloc',
  'kermit',
  'kilobyte',
  'lan',
  'lsi',
  'latency',
  'loader',
  'mb',
  'mhz',
  'mips',
  'mos',
  'mosfet',
  'msi',
  'mtbf',
  'mttr',
  'mttf',
  'macro',
  'macroinstruction',
  'mainframe',
  'maintainability',
  'maintenance',
  'measurable',
  'measurement',
  'megabit',
  'megabyte',
  'megahertz',
  'menu',
  'microcode',
  'microcomputer',
  'microprocessor',
  'minicomputer',
  'mishap',
  'mnemonic',
  'modeling',
  'modem',
  'modifiability',
  'modularity',
  'modulate',
  'modulation',
  'module',
  'multitask',
  'multiplexer',
  'nbs',
  'nist',
  'nmi',
  'nmos',
  'nibble',
  'node',
  'null',
  'ocr',
  'oem',
  'oop',
  'octal',
  'online',
  'operator',
  'optimization',
  'oracle',
  'overflow',
  'pal',
  'pc',
  'pcb',
  'pdl',
  'pla',
  'pld',
  'pmos',
  'prom',
  'paging',
  'parameter',
  'parity',
  'pascal',
  'password',
  'pixel',
  'polling',
  'precision',
  'program',
  'protocol',
  'prototyping',
  'pseudocode',
  'qa',
  'qc',
  'ram',
  'rfi',
  'risc',
  'rom',
  'recursion',
  'reliability',
  'requirement',
  'revalidation',
  'robustness',
  'scsi',
  'sops',
  'sql',
  'ssi',
  'safety',
  'security',
  'sensor',
  'serial',
  'server',
  'servomechanism',
  'severity',
  'simulation',
  'simulator',
  'sizing',
  'specification',
  'stub',
  'subprogram',
  'subroutine',
  'synchronous',
  'syntax',
  'tb',
  'terabyte',
  'testability',
  'testing',
  'timing',
  'traceability',
  'transaction',
  'translation',
  'tuning',
  'unambiguous',
  'underflow',
  'unix',
  'usability',
  'user',
  'v&v',
  'vax',
  'vlsi',
  'vms',
  'vv&t',
  'validate',
  'validation',
  'variable',
  'vendor',
  'verifiable',
  'verify',
  'virus',
  'wan',
  'walkthrough',
  'workaround',
  'workstation',
  'worm',
  'xmodem',
  'ymodem',
  'zmodem',
  'action',
  'ability',
  'ache',
  'activity',
  'actually',
  'admiral',
  'advertisement',
  'advice',
  'afterward',
  'alarm',
  'alphabet',
  'amateur',
  'amazing',
  'ancestor',
  'angry',
  'anymore',
  'anyway',
  'anywhere',
  'appetite',
  'appointment',
  'ashamed',
  'astronaut',
  'attendant',
  'autumn',
  'badly',
  'basement',
  'bedside',
  'beggar',
  'being',
  'belong to',
  'belt',
  'besides',
  'bike',
  'blond',
  'boast',
  'bookstore',
  'booth',
  'boring',
  'bound',
  'british',
  'bucket',
  'bulb',
  'butterfly',
  'campaign',
  'canal',
  'candle',
  'careless',
  'cemetery',
  'center',
  'central',
  'ceremony',
  'chairperson',
  'charming',
  'cheerful',
  'childhood',
  'chimney',
  'choice',
  'chopstick',
  'classical',
  'clone',
  'closet',
  'clothes',
  'cloudy',
  'coeducation',
  'coil',
  'colorful',
  'comb',
  'common',
  'communication',
  'competition',
  'composer',
  'concentration',
  'congratulation',
  'correctly',
  'course',
  'crowded',
  'cyber space',
  'dangerous',
  'darkness',
  'deaf',
  'dear',
  'deer',
  'delcare',
  'dessert',
  'detective',
  'development',
  'diet',
  'difficulty',
  'diligent',
  'dining room',
  'direction',
  'dirt',
  'disgusting',
  'distance',
  'ditch',
  'dollar',
  'downtown',
  'dragon',
  'drugstore',
  'drum',
  'dumb',
  'dye',
  'dynasty',
  'earthquake',
  'easily',
  'eastern',
  'education',
  'elder',
  'election',
  'electricity',
  'endangered',
  'energy',
  'engineering',
  'event',
  'everyday',
  'everything',
  'everywhere',
  'exactly',
  'excited',
  'exciting',
  'exhibition',
  'expressway',
  'fairy',
  'faithful',
  'fantasy',
  'feast',
  'feather',
  'fence',
  'finally',
  'firewood',
  'fitness',
  'flashlight',
  'flea',
  'flour',
  'flute',
  'foggy',
  'foolish',
  'foreigner',
  'fortunately',
  'fountain pen',
  'freedom',
  'french',
  'friendly',
  'friendship',
  'frighten',
  'funny',
  'further',
  'gallery',
  'garbage',
  'gay',
  'german',
  'government',
  'grammar',
  'greedy',
  'greenhouse',
  'greeting',
  'growth',
  'guard',
  'happily',
  'happiness',
  'hard-working',
  'harmful',
  'harmony',
  'hastily',
  'hawk',
  'hay',
  'hike',
  'historic',
  'hollow',
  'homeless',
  'homesick',
  'hometown',
  'hop',
  'hopeless',
  'howl',
  'hunger',
  'hunter',
  'hurrah',
  'hydrogen',
  'iceberg',
  'image',
  'imagination',
  'immediately',
  'importance',
  'independence',
  'industrial',
  'information',
  'inner',
  'interesting',
  'invention',
  'invitation',
  'issue',
  'ivy',
  'jean',
  'jewel',
  'kindergarten',
  'kindness',
  'kite',
  'knowledge',
  'lantern',
  'later',
  'least',
  'life',
  'life ',
  'lifelong',
  'lifetime',
  'living room',
  'loaf',
  'lonely',
  'lovely',
  'lower',
  'lucky',
  'lung',
  'magic',
  'mammal',
  'mankind',
  'marble',
  'march',
  'mars',
  'meaning',
  'merry',
  'midnight',
  'mile',
  'mine',
  'model',
  'monk',
  'most',
  'mostly',
  'musician',
  'mystery',
  'nap',
  'national',
  'natural',
  'nearby',
  'nearly',
  'neighborhood',
  'nickname',
  'niece',
  'noisy',
  'northern',
  'oath',
  'opposite',
  'original',
  'ostrich',
  'overhear',
  'owl',
  'oxygen',
  'parade',
  'partly',
  'party',
  'peaceful',
  'personal',
  'petal',
  'photo',
  'photographer',
  'pigeon',
  'pilgrim',
  'pilot',
  'politely',
  'pollution',
  'portable',
  'powerful',
  'prairie',
  'product',
  'professional',
  'project',
  'pronunciation',
  'properly',
  'proverb',
  'pulse',
  'pumpkin',
  'puritan',
  'quickly',
  'rapidly',
  'rarely',
  'really',
  'recently',
  'refresh',
  'relay',
  'religious',
  'revival',
  'riddle',
  'robber',
  'rudely',
  'salty',
  'sample',
  'saw',
  'saying',
  'scar',
  'scenery',
  'schedule',
  'scientific',
  'sculptor',
  'second',
  'section',
  'seldom',
  'semester',
  'servant',
  'settler',
  'shot',
  'shrug',
  'signal',
  'silence',
  'simply',
  'sincerely',
  'skyscraper',
  'solar',
  'solution',
  'somebody',
  'sometime',
  'sometimes',
  'somewhere',
  'sophomore',
  'sow',
  'spaceship',
  'spade',
  'spring',
  'squirrel',
  'stadium',
  'stair',
  'stormy',
  'stranger',
  'strength',
  'stupid',
  'success',
  'successful',
  'suddenly',
  'suitcase',
  'sunrise',
  'sunshine',
  'superstition',
  'surf',
  'sword',
  'technology',
  'teenager',
  'thermometer',
  'thin ',
  'thirsty',
  'ticket',
  'tightly',
  'toll',
  'tomb',
  'torch',
  'tourist',
  'towel',
  'track',
  'traditional',
  'trash',
  'trousers',
  'trumpet',
  'turkey',
  'turtle',
  'type ',
  'uncomfortable',
  'unification',
  'unity',
  'unknown',
  'unlike',
  'unusual',
  'upside down',
  'useful',
  'useless',
  'usually',
  'vain',
  'valuable',
  'visitor',
  'volunteer',
  'western',
  'whisker',
  'wink',
  'wisdom',
  'wolf',
  'wonderful',
  'yard',
  'player'
];


export const NationPhoneCodes: {
  callingCode: string;
  countryName: {
    ko: string;
    en: string;
  }
}[] = [
{ callingCode: '+233', countryName: { ko: '가나',  en: 'Ghana' } },
{ callingCode: '+241', countryName: { ko: '가봉',  en: 'Gabon' } },
{ callingCode: '+592', countryName: { ko: '가이아나',  en: 'Guyana' } },
{ callingCode: '+220', countryName: { ko: '감비아',  en: 'Gambia' } },
{ callingCode: '+44', countryName: { ko: '건지',  en: 'Guernsey' } },
{ callingCode: '+590', countryName: { ko: '과들루프',  en: 'Guadeloupe' } },
{ callingCode: '+502', countryName: { ko: '과테말라',  en: 'Guatemala' } },
{ callingCode: '+30', countryName: { ko: '그리스',  en: 'Greece' } },
{ callingCode: '+299', countryName: { ko: '그린란드',  en: 'Greenland' } },
{ callingCode: '+224', countryName: { ko: '기니',  en: 'Guinea' } },
{ callingCode: '+245', countryName: { ko: '기니비사우',  en: 'Guinea-Bissau' } },
{ callingCode: '+264', countryName: { ko: '나미비아',  en: 'Namibia' } },
{ callingCode: '+674', countryName: { ko: '나우루',  en: 'Nauru' } },
{ callingCode: '+234', countryName: { ko: '나이지리아',  en: 'Nigeria' } },
{ callingCode: '+672', countryName: { ko: '남극 대륙',  en: 'Antarctica' } },
{ callingCode: '+211', countryName: { ko: '남수단',  en: 'South Sudan' } },
{ callingCode: '+27', countryName: { ko: '남아프리카 공화국',  en: 'South Africa' } },
{ callingCode: '+31', countryName: { ko: '네덜란드',  en: 'Netherlands' } },
{ callingCode: '+977', countryName: { ko: '네팔',  en: 'Nepal' } },
{ callingCode: '+47', countryName: { ko: '노르웨이',  en: 'Norway' } },
{ callingCode: '+672', countryName: { ko: '노퍽섬',  en: 'Norfolk Island' } },
{ callingCode: '+687', countryName: { ko: '누벨칼레도니',  en: 'New Caledonia' } },
{ callingCode: '+64', countryName: { ko: '뉴질랜드',  en: 'New Zealand' } },
{ callingCode: '+683', countryName: { ko: '니우에',  en: 'Niue' } },
{ callingCode: '+227', countryName: { ko: '니제르',  en: 'Niger' } },
{ callingCode: '+505', countryName: { ko: '니카라과',  en: 'Nicaragua' } },
{ callingCode: '+886', countryName: { ko: '대만',  en: 'Taiwan' } },
{ callingCode: '+82', countryName: { ko: '대한민국',  en: 'South Korea' } },
{ callingCode: '+45', countryName: { ko: '덴마크',  en: 'Denmark' } },
{ callingCode: '+49', countryName: { ko: '독일',  en: 'Germany' } },
{ callingCode: '+670', countryName: { ko: '동티모르',  en: 'East Timor' } },
{ callingCode: '+856', countryName: { ko: '라오스',  en: 'Laos' } },
{ callingCode: '+231', countryName: { ko: '라이베리아',  en: 'Liberia' } },
{ callingCode: '+371', countryName: { ko: '라트비아',  en: 'Latvia' } },
{ callingCode: '+7', countryName: { ko: '러시아',  en: 'Russia' } },
{ callingCode: '+961', countryName: { ko: '레바논',  en: 'Lebanon' } },
{ callingCode: '+266', countryName: { ko: '레소토',  en: 'Lesotho' } },
{ callingCode: '+262', countryName: { ko: '레위니옹',  en: 'Reunion' } },
{ callingCode: '+40', countryName: { ko: '루마니아',  en: 'Romania' } },
{ callingCode: '+352', countryName: { ko: '룩셈부르크',  en: 'Luxembourg' } },
{ callingCode: '+250', countryName: { ko: '르완다',  en: 'Rwanda' } },
{ callingCode: '+218', countryName: { ko: '리비아',  en: 'Libya' } },
{ callingCode: '+370', countryName: { ko: '리투아니아',  en: 'Lithuania' } },
{ callingCode: '+423', countryName: { ko: '리히텐슈타인',  en: 'Liechtenstein' } },
{ callingCode: '+261', countryName: { ko: '마다가스카르',  en: 'Madagascar' } },
{ callingCode: '+596', countryName: { ko: '마르티니크',  en: 'Martinique' } },
{ callingCode: '+692', countryName: { ko: '마셜 제도',  en: 'Marshall Islands' } },
{ callingCode: '+269', countryName: { ko: '마요트',  en: 'Mayotte' } },
{ callingCode: '+853', countryName: { ko: '마카오',  en: 'Macau SAR China' } },
{ callingCode: '+265', countryName: { ko: '말라위',  en: 'Malawi' } },
{ callingCode: '+60', countryName: { ko: '말레이시아',  en: 'Malaysia' } },
{ callingCode: '+223', countryName: { ko: '말리',  en: 'Mali' } },
{ callingCode: '+44', countryName: { ko: '맨섬',  en: 'Isle of Man' } },
{ callingCode: '+52', countryName: { ko: '멕시코',  en: 'Mexico' } },
{ callingCode: '+377', countryName: { ko: '모나코',  en: 'Monaco' } },
{ callingCode: '+212', countryName: { ko: '모로코',  en: 'Morocco' } },
{ callingCode: '+230', countryName: { ko: '모리셔스',  en: 'Mauritius' } },
{ callingCode: '+222', countryName: { ko: '모리타니',  en: 'Mauritania' } },
{ callingCode: '+258', countryName: { ko: '모잠비크',  en: 'Mozambique' } },
{ callingCode: '+382', countryName: { ko: '몬테네그로',  en: 'Montenegro' } },
{ callingCode: '+373', countryName: { ko: '몰도바',  en: 'Moldova' } },
{ callingCode: '+960', countryName: { ko: '몰디브',  en: 'Maldives' } },
{ callingCode: '+356', countryName: { ko: '몰타',  en: 'Malta' } },
{ callingCode: '+976', countryName: { ko: '몽골',  en: 'Mongolia' } },
{ callingCode: '+1', countryName: { ko: '미국',  en: 'United States' } },
{ callingCode: '+699', countryName: { ko: '미국령 군소 제도',  en: 'United States Minor Outlying Islands' } },
{ callingCode: '+95', countryName: { ko: '미얀마',  en: 'Burma (Myanmar)' } },
{ callingCode: '+691', countryName: { ko: '미크로네시아 연방',  en: 'Micronesia' } },
{ callingCode: '+678', countryName: { ko: '바누아투',  en: 'Vanuatu' } },
{ callingCode: '+973', countryName: { ko: '바레인',  en: 'Bahrain' } },
{ callingCode: '+39', countryName: { ko: '바티칸',  en: 'Holy See (Vatican City)' } },
{ callingCode: '+880', countryName: { ko: '방글라데시',  en: 'Bangladesh' } },
{ callingCode: '+229', countryName: { ko: '베냉',  en: 'Benin' } },
{ callingCode: '+58', countryName: { ko: '베네수엘라',  en: 'Venezuela' } },
{ callingCode: '+84', countryName: { ko: '베트남',  en: 'Vietnam' } },
{ callingCode: '+32', countryName: { ko: '벨기에',  en: 'Belgium' } },
{ callingCode: '+375', countryName: { ko: '벨라루스',  en: 'Belarus' } },
{ callingCode: '+501', countryName: { ko: '벨리즈',  en: 'Belize' } },
{ callingCode: '+387', countryName: { ko: '보스니아 헤르체고비나',  en: 'Bosnia and Herzegovina' } },
{ callingCode: '+267', countryName: { ko: '보츠와나',  en: 'Botswana' } },
{ callingCode: '+591', countryName: { ko: '볼리비아',  en: 'Bolivia' } },
{ callingCode: '+257', countryName: { ko: '부룬디',  en: 'Burundi' } },
{ callingCode: '+226', countryName: { ko: '부르키나파소',  en: 'Burkina Faso' } },
{ callingCode: '+975', countryName: { ko: '부탄',  en: 'Bhutan' } },
{ callingCode: '+389', countryName: { ko: '북마케도니아',  en: 'Macedonia' } },
{ callingCode: '+359', countryName: { ko: '불가리아',  en: 'Bulgaria' } },
{ callingCode: '+55', countryName: { ko: '브라질',  en: 'Brazil' } },
{ callingCode: '+673', countryName: { ko: '브루나이',  en: 'Brunei' } },
{ callingCode: '+685', countryName: { ko: '사모아',  en: 'Samoa' } },
{ callingCode: '+966', countryName: { ko: '사우디아라비아',  en: 'Saudi Arabia' } },
{ callingCode: '+378', countryName: { ko: '산마리노',  en: 'San Marino' } },
{ callingCode: '+239', countryName: { ko: '상투메 프린시페',  en: 'Sao Tome and Principe' } },
{ callingCode: '+590', countryName: { ko: '생바르텔레미',  en: 'Saint Barthelemy' } },
{ callingCode: '+508', countryName: { ko: '생피에르 미클롱',  en: 'Saint Pierre and Miquelon' } },
{ callingCode: '+212', countryName: { ko: '서사하라',  en: 'Western Sahara' } },
{ callingCode: '+221', countryName: { ko: '세네갈',  en: 'Senegal' } },
{ callingCode: '+381', countryName: { ko: '세르비아',  en: 'Serbia' } },
{ callingCode: '+248', countryName: { ko: '세이셸',  en: 'Seychelles' } },
{ callingCode: '+290', countryName: { ko: '세인트헬레나',  en: 'Saint Helena' } },
{ callingCode: '+252', countryName: { ko: '소말리아',  en: 'Somalia' } },
{ callingCode: '+677', countryName: { ko: '솔로몬 제도',  en: 'Solomon Islands' } },
{ callingCode: '+249', countryName: { ko: '수단',  en: 'Sudan' } },
{ callingCode: '+597', countryName: { ko: '수리남',  en: 'Suriname' } },
{ callingCode: '+94', countryName: { ko: '스리랑카',  en: 'Sri Lanka' } },
{ callingCode: '+47', countryName: { ko: '스발바르 얀마옌 제도',  en: 'Svalbard and Jan Mayen' } },
{ callingCode: '+46', countryName: { ko: '스웨덴',  en: 'Sweden' } },
{ callingCode: '+41', countryName: { ko: '스위스',  en: 'Switzerland' } },
{ callingCode: '+34', countryName: { ko: '스페인',  en: 'Spain' } },
{ callingCode: '+421', countryName: { ko: '슬로바키아',  en: 'Slovakia' } },
{ callingCode: '+386', countryName: { ko: '슬로베니아',  en: 'Slovenia' } },
{ callingCode: '+963', countryName: { ko: '시리아',  en: 'Syria' } },
{ callingCode: '+232', countryName: { ko: '시에라리온',  en: 'Sierra Leone' } },
{ callingCode: '+599', countryName: { ko: '신트마르턴',  en: 'Sint Maarten' } },
{ callingCode: '+65', countryName: { ko: '싱가포르',  en: 'Singapore' } },
{ callingCode: '+971', countryName: { ko: '아랍에미리트',  en: 'United Arab Emirates' } },
{ callingCode: '+297', countryName: { ko: '아루바',  en: 'Aruba' } },
{ callingCode: '+374', countryName: { ko: '아르메니아',  en: 'Armenia' } },
{ callingCode: '+54', countryName: { ko: '아르헨티나',  en: 'Argentina' } },
{ callingCode: '+354', countryName: { ko: '아이슬란드',  en: 'Iceland' } },
{ callingCode: '+509', countryName: { ko: '아이티',  en: 'Haiti' } },
{ callingCode: '+353', countryName: { ko: '아일랜드',  en: 'Ireland' } },
{ callingCode: '+994', countryName: { ko: '아제르바이잔',  en: 'Azerbaijan' } },
{ callingCode: '+93', countryName: { ko: '아프가니스탄',  en: 'Afghanistan' } },
{ callingCode: '+376', countryName: { ko: '안도라',  en: 'Andorra' } },
{ callingCode: '+355', countryName: { ko: '알바니아',  en: 'Albania' } },
{ callingCode: '+213', countryName: { ko: '알제리',  en: 'Algeria' } },
{ callingCode: '+244', countryName: { ko: '앙골라',  en: 'Angola' } },
{ callingCode: '+291', countryName: { ko: '에리트레아',  en: 'Eritrea' } },
{ callingCode: '+268', countryName: { ko: '에스와티니',  en: 'Swaziland' } },
{ callingCode: '+372', countryName: { ko: '에스토니아',  en: 'Estonia' } },
{ callingCode: '+593', countryName: { ko: '에콰도르',  en: 'Ecuador' } },
{ callingCode: '+251', countryName: { ko: '에티오피아',  en: 'Ethiopia' } },
{ callingCode: '+503', countryName: { ko: '엘살바도르',  en: 'El Salvador' } },
{ callingCode: '+44', countryName: { ko: '영국',  en: 'United Kingdom' } },
{ callingCode: '+246', countryName: { ko: '영국령 인도양 지역',  en: 'British Indian Ocean Territory' } },
{ callingCode: '+967', countryName: { ko: '예멘',  en: 'Yemen' } },
{ callingCode: '+968', countryName: { ko: '오만',  en: 'Oman' } },
{ callingCode: '+61', countryName: { ko: '오스트레일리아',  en: 'Australia' } },
{ callingCode: '+61', countryName: { ko: '오스트레일리아',  en: 'Australia' } },
{ callingCode: '+43', countryName: { ko: '오스트리아',  en: 'Austria' } },
{ callingCode: '+504', countryName: { ko: '온두라스',  en: 'Honduras' } },
{ callingCode: '+681', countryName: { ko: '왈리스 푸투나',  en: 'Wallis and Futuna' } },
{ callingCode: '+962', countryName: { ko: '요르단',  en: 'Jordan' } },
{ callingCode: '+256', countryName: { ko: '우간다',  en: 'Uganda' } },
{ callingCode: '+598', countryName: { ko: '우루과이',  en: 'Uruguay' } },
{ callingCode: '+998', countryName: { ko: '우즈베키스탄',  en: 'Uzbekistan' } },
{ callingCode: '+380', countryName: { ko: '우크라이나',  en: 'Ukraine' } },
{ callingCode: '+964', countryName: { ko: '이라크',  en: 'Iraq' } },
{ callingCode: '+98', countryName: { ko: '이란',  en: 'Iran' } },
{ callingCode: '+972', countryName: { ko: '이스라엘',  en: 'Israel' } },
{ callingCode: '+20', countryName: { ko: '이집트',  en: 'Egypt' } },
{ callingCode: '+39', countryName: { ko: '이탈리아',  en: 'Italy' } },
{ callingCode: '+91', countryName: { ko: '인도',  en: 'India' } },
{ callingCode: '+62', countryName: { ko: '인도네시아',  en: 'Indonesia' } },
{ callingCode: '+81', countryName: { ko: '일본',  en: 'Japan' } },
{ callingCode: '+260', countryName: { ko: '잠비아',  en: 'Zambia' } },
{ callingCode: '+44', countryName: { ko: '저지섬',  en: 'Jersey' } },
{ callingCode: '+240', countryName: { ko: '적도 기니',  en: 'Equatorial Guinea' } },
{ callingCode: '+995', countryName: { ko: '조지아',  en: 'Georgia' } },
{ callingCode: '+36', countryName: { ko: '중앙아프리카 공화국',  en: 'Central African Republic' } },
{ callingCode: '+86', countryName: { ko: '중국',  en: 'China' } },
{ callingCode: '+253', countryName: { ko: '지부티',  en: 'Djibouti' } },
{ callingCode: '+350', countryName: { ko: '지브롤터',  en: 'Gibraltar' } },
{ callingCode: '+263', countryName: { ko: '짐바브웨',  en: 'Zimbabwe' } },
{ callingCode: '+235', countryName: { ko: '차드',  en: 'Chad' } },
{ callingCode: '+420', countryName: { ko: '체코',  en: 'Czech Republic' } },
{ callingCode: '+56', countryName: { ko: '칠레',  en: 'Chile' } },
{ callingCode: '+237', countryName: { ko: '카메룬',  en: 'Cameroon' } },
{ callingCode: '+238', countryName: { ko: '카보베르데',  en: 'Cape Verde' } },
{ callingCode: '+7', countryName: { ko: '카자흐스탄',  en: 'Kazakhstan' } },
{ callingCode: '+974', countryName: { ko: '카타르',  en: 'Qatar' } },
{ callingCode: '+855', countryName: { ko: '캄보디아',  en: 'Cambodia' } },
{ callingCode: '+1', countryName: { ko: '캐나다',  en: 'Canada' } },
{ callingCode: '+254', countryName: { ko: '케냐',  en: 'Kenya' } },
{ callingCode: '+506', countryName: { ko: '코스타리카',  en: 'Costa Rica' } },
{ callingCode: '+61', countryName: { ko: '코코스 제도',  en: 'Cocos (Keeling) Islands' } },
{ callingCode: '+225', countryName: { ko: '코트디부아르',  en: 'Ivory Coast' } },
{ callingCode: '+57', countryName: { ko: '콜롬비아',  en: 'Colombia' } },
{ callingCode: '+242', countryName: { ko: '콩고 - 브라자빌',  en: 'Congo - Brazzaville' } },
{ callingCode: '+243', countryName: { ko: '콩고 - 킨샤사',  en: 'Congo - Kinshasa' } },
{ callingCode: '+53', countryName: { ko: '쿠바',  en: 'Cuba' } },
{ callingCode: '+965', countryName: { ko: '쿠웨이트',  en: 'Kuwait' } },
{ callingCode: '+682', countryName: { ko: '쿡 제도',  en: 'Cook Islands' } },
{ callingCode: '+599', countryName: { ko: '퀴라소',  en: 'Curacao' } },
{ callingCode: '+385', countryName: { ko: '크로아티아',  en: 'Croatia' } },
{ callingCode: '+61', countryName: { ko: '크리스마스섬',  en: 'Christmas Island' } },
{ callingCode: '+996', countryName: { ko: '키르기스스탄',  en: 'Kyrgyzstan' } },
{ callingCode: '+686', countryName: { ko: '키리바시',  en: 'Kiribati' } },
{ callingCode: '+357', countryName: { ko: '키프로스',  en: 'Cyprus' } },
{ callingCode: '+992', countryName: { ko: '타지키스탄',  en: 'Tajikistan' } },
{ callingCode: '+255', countryName: { ko: '탄자니아',  en: 'Tanzania' } },
{ callingCode: '+66', countryName: { ko: '태국',  en: 'Thailand' } },
{ callingCode: '+90', countryName: { ko: '터키',  en: 'Turkey' } },
{ callingCode: '+228', countryName: { ko: '토고',  en: 'Togo' } },
{ callingCode: '+690', countryName: { ko: '토켈라우',  en: 'Tokelau' } },
{ callingCode: '+676', countryName: { ko: '통가',  en: 'Tonga' } },
{ callingCode: '+993', countryName: { ko: '투르크메니스탄',  en: 'Turkmenistan' } },
{ callingCode: '+688', countryName: { ko: '투발루',  en: 'Tuvalu' } },
{ callingCode: '+216', countryName: { ko: '튀니지',  en: 'Tunisia' } },
{ callingCode: '+507', countryName: { ko: '파나마',  en: 'Panama' } },
{ callingCode: '+595', countryName: { ko: '파라과이',  en: 'Paraguay' } },
{ callingCode: '+92', countryName: { ko: '파키스탄',  en: 'Pakistan' } },
{ callingCode: '+675', countryName: { ko: '파푸아뉴기니',  en: 'Papua New Guinea' } },
{ callingCode: '+680', countryName: { ko: '팔라우',  en: 'Palau' } },
{ callingCode: '+298', countryName: { ko: '페로 제도',  en: 'Faroe Islands' } },
{ callingCode: '+51', countryName: { ko: '페루',  en: 'Peru' } },
{ callingCode: '+351', countryName: { ko: '포르투갈',  en: 'Portugal' } },
{ callingCode: '+500', countryName: { ko: '포클랜드 제도',  en: 'Falkland Islands' } },
{ callingCode: '+48', countryName: { ko: '폴란드',  en: 'Poland' } },
{ callingCode: '+1', countryName: { ko: '푸에르토리코',  en: 'Puerto Rico' } },
{ callingCode: '+33', countryName: { ko: '프랑스',  en: 'France' } },
{ callingCode: '+594', countryName: { ko: '프랑스령 기아나',  en: 'French Guiana' } },
{ callingCode: '+689', countryName: { ko: '프랑스령 폴리네시아',  en: 'French Polynesia' } },
{ callingCode: '+679', countryName: { ko: '피지',  en: 'Fiji' } },
{ callingCode: '+358', countryName: { ko: '핀란드',  en: 'Finland' } },
{ callingCode: '+63', countryName: { ko: '필리핀',  en: 'Philippines' } },
{ callingCode: '+63', countryName: { ko: '필리핀',  en: 'Philippines' } },
{ callingCode: '+870', countryName: { ko: '핏케언 제도',  en: 'Pitcairn Islands' } },
{ callingCode: '+36', countryName: { ko: '헝가리',  en: 'Hungary' } },
{ callingCode: '+852', countryName: { ko: '홍콩',  en: 'Hong Kong' } }];

